import { Component, inject, OnInit } from '@angular/core';
import { TranslocoDirective, TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ButtonComponent,
  FormSectionComponent,
  TextFieldWithErrorComponent
} from 'htx-ui-library';
import { ProtectedContentComponent } from '../../../../organisms/protected-content/protected-content.component';
import { NavigationService } from '../../../../../services/navigation.service';
import { NotificationService } from '../../../../../services/form/notification.service';
import { FormErrorMessagesService } from '../../../../../services/form/form-error-messages.service';
import { DsaUserService } from '../../../../../services/dsa-users/dsa-user.service';
import { DsaUser } from '../../../../../services/dsa-users/dsa-user.model';

@Component({
  selector: 'app-dsa-user-edit-page',
  imports: [
    TranslocoDirective,
    TranslocoPipe,
    ReactiveFormsModule,
    ButtonComponent,
    FormSectionComponent,
    TextFieldWithErrorComponent,
    ProtectedContentComponent
  ],
  templateUrl: './dsa-user-edit-page.component.html',
  styleUrl: './dsa-user-edit-page.component.scss'
})
export class DsaUserEditPageComponent implements OnInit {
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private dsaUserService = inject(DsaUserService);
  private notificationService = inject(NotificationService);
  private translocoService = inject(TranslocoService);
  private navigationService = inject(NavigationService);
  readonly errorMessagesService = inject(FormErrorMessagesService);

  requiredRoles = this.navigationService.getAccessRoles('admin/users');
  fieldLabels: { [key: string]: string } = {};
  saving = false;
  iamId = '';

  form = this.fb.group({
    firstName: ['', [Validators.required, Validators.maxLength(50)]],
    lastName: ['', [Validators.required, Validators.maxLength(50)]],
    email: ['', [Validators.required, Validators.email, Validators.maxLength(100)]]
  });

  ngOnInit(): void {
    this.iamId = this.route.snapshot.paramMap.get('iamId') ?? '';

    const controlNames = ['firstName', 'lastName', 'email'];
    controlNames.forEach((name) => {
      this.fieldLabels[name] = this.translocoService.translate(
        `dsaManagement.users.form.${name}`
      );
    });

    this.dsaUserService.findAll().subscribe({
      next: (users) => {
        const user = users.find((u) => u.iamId === this.iamId);
        if (user) {
          this.form.patchValue({
            firstName: user.firstName,
            lastName: user.lastName,
            email: user.email
          });
        } else {
          this.notificationService.showError('dsaManagement.users.loadError', 5000);
          void this.router.navigate(['admin', 'users']);
        }
      },
      error: () => {
        this.notificationService.showError('dsaManagement.users.loadError', 5000);
        void this.router.navigate(['admin', 'users']);
      }
    });
  }

  save(): void {
    if (this.form.invalid || this.saving) {
      return;
    }

    const value = this.form.getRawValue();
    const updatedUser: DsaUser = {
      firstName: value.firstName ?? '',
      lastName: value.lastName ?? '',
      email: value.email ?? ''
    };

    this.saving = true;
    this.dsaUserService.update(this.iamId, updatedUser).subscribe({
      next: () => {
        this.saving = false;
        this.notificationService.showSuccess('dsaManagement.users.edit.saveSuccess', 5000);
        void this.router.navigate(['admin', 'users']);
      },
      error: () => {
        this.saving = false;
        this.notificationService.showError('dsaManagement.users.edit.saveError', 5000);
      }
    });
  }

  cancel(): void {
    void this.router.navigate(['admin', 'users']);
  }
}
